import { createMuiTheme, responsiveFontSizes } from "@material-ui/core";

// colors
const primary = "#204490";
const secondary = "#e3e6ec";
const red = "#DA5D65";
const black = "#343a40";
const darkBlack = "rgb(36, 40, 44)";
const background = "#f5f5f5";
const backgroundLightGray = "#ECF0F3";
const backgroundLightGrayV2 = "#F5F6F8";
const gray = "#D9DDE0";

const warningLight = "rgba(253, 200, 69, .3)";
const warningMain = "rgba(253, 200, 69, .5)";
const warningDark = "rgba(253, 200, 69, .7)";

// border
const borderWidth = 2;
const borderColor = "rgba(0, 0, 0, 0.13)";

// breakpoints
const xl = 1920;
const lg = 1280;
const md = 960;
const sm = 600;
const xs = 0;

// spacing
const spacing = 8;

// headings
const h1 = "48px !important";
const h2 = "32px !important";
const h3 = "24px !important";
const h4 = "20px !important";
const h5 = "16px !important";  //open sans
const h6 = "14px !important";  //open sans

const theme = createMuiTheme({
  palette: {
    primary: { main: primary },
    secondary: { main: secondary },
    common: {
      black,
      darkBlack,
      red,
      gray
    },
    warning: {
      light: warningLight,
      main: warningMain,
      dark: warningDark
    },
    // Used to shift a color's luminance by approximately
    // two indexes within its tonal palette.
    // E.g., shift from Red 500 to Red 300 or Red 700.
    tonalOffset: 0.2,
    background: {
      default: background,
      backgroundLightGray: backgroundLightGray,
      backgroundDarkGray: gray,
      backgroundLightGrayV2: backgroundLightGrayV2
    },
    spacing
  },
  breakpoints: {
    // Define custom breakpoint values.
    // These will apply to Material-UI components that use responsive
    // breakpoints, such as `Grid` and `Hidden`. You can also use the
    // theme breakpoint functions `up`, `down`, and `between` to create
    // media queries for these breakpoints
    values: {
      xs,
      sm,
      md,
      lg,
      xl,
      // lg,
      // md,
      // sm,
      // xs
    }
  },
  border: {
    borderColor: borderColor,
    borderWidth: borderWidth
  },
  overrides: {
    MUIRichTextEditor: {
      root: {
        backgroundColor: backgroundLightGrayV2,
      },
      container: {
        display: "flex",
        flexDirection: "column-reverse"
      },
      editor: {
        paddingLeft: 20,
        backgroundColor: backgroundLightGrayV2,
        ".title-wrapper &": {
          height: '40px !important',
        },
        height: "100px",
        // maxHeight: "200px",
        overflow: "auto",
        // border: "1px solid #c3c3c3"
      },
      toolbar: {
        backgroundColor: "#fff",
      },
      placeHolder: {
        paddingLeft: 20,
        width: "inherit",
        position: "absolute",
        top: "20px",
        ".title-wrapper &": {
          top: '0px !important'
        }
      },
      anchorLink: {
        color: "#333333",
        textDecoration: "underline",

      }
    },
    MuiLink: {
      root: {
        display: 'inline-block'
      }
    },
    MuiTypography: {
      h1: {
        fontSize: h1,
        fontWeight: 'normal',
      },
      h2: {
        fontSize: h2,
        fontWeight: 400,
      },
      h3: {
        fontSize: h3,
      },
      h4: {
        fontSize: h4,
      },
      h5: {
        fontSize: h5,
      },
      h6: {
        fontSize: h6,
      }
    },
    MuiInput: {
      root: {
        "&:before": {
          borderBottom: 'none !important'
        }
      },
    },
    MuiChip: {
      root: {
        color: red,
        backgroundColor: "#fff",
        fontWeight: 400,
        fontSize: 16,
        margin: "5px 8px",
        "& svg path": {
          color: red
        },
      }
    },
    MuiDrawer: {
      paperAnchorLeft: {
        backgroundColor: primary
      }
    },
    MuiExpansionPanelSummary: {
      root: {
        padding: 0,
      },
    },
    MuiButton: {
      root: {
        borderRadius: 20,
        padding: '3px 15px !important',
        fontSize: '0.9rem',
        textTransform: 'unset',
      },
      contained: {
        backgroundColor: '#fff',
        color: "#204490",
      }
    },
    MuiSvgIcon: {
      root: {
        width: '1em',
        height: '1em',
      }
    },
    MuiExpansionPanel: {
      root: {
        position: "static"
      }
    },
    MuiTableContainer: {
      root: {
        height: '400px !important'
      },
    },
    MuiTab: {
      labelIcon: {
        minHeight: 0
      },
    },
    MuiTableCell: {
      root: {
        paddingLeft: spacing * 2,
        paddingRight: spacing * 2,
        borderBottom: `${borderWidth}px solid ${borderColor}`,
        [`@media (max-width:  ${sm}px)`]: {
          paddingLeft: spacing,
          paddingRight: spacing
        }
      }
    },
    MuiDivider: {
      root: {
        backgroundColor: borderColor,
        height: borderWidth
      }
    },
    MuiPrivateNotchedOutline: {
      root: {
        borderWidth: borderWidth
      }
    },
    MuiListItem: {
      divider: {
        borderBottom: `${borderWidth}px solid ${borderColor}`
      }
    },
    MuiDialog: {
      paper: {
        width: "100%",
        maxWidth: 430,
        marginLeft: spacing,
        marginRight: spacing
      }
    },
    MuiTooltip: {
      tooltip: {
        backgroundColor: darkBlack
      }
    },
    MuiExpansionPanelDetails: {
      root: {
        [`@media (max-width:  ${sm}px)`]: {
          paddingLeft: spacing,
          paddingRight: spacing
        },
        backgroundColor: primary,
      }
    },
  },
  typography: {
    useNextVariants: true,
    body1: {
      // color: primary,
      // lineHeight: 1,
    },
    body2: {
      color: primary,
      fontStyle: 'normal',
      fontWeight: 'normal',
      fontSize: 16,
    },
    h6: {
      color: primary,
    },
    subtitle1: {
      color: secondary,
      fontStyle: 'italic',
      letterSpacing: '1px!important',
      fontSize: '0.85rem',

    },
    subtitle2: {
      color: primary,
      fontStyle: 'italic',
      letterSpacing: '1px!important',
      fontSize: '0.85rem',
    },
  }
});

export default responsiveFontSizes(theme);
